import React, { useEffect, useRef } from 'react';
import { useEditor, EditorContent as TiptapContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import TaskList from '@tiptap/extension-task-list';
import TaskItem from '@tiptap/extension-task-item';
import Table from '@tiptap/extension-table';
import Link from '@tiptap/extension-link';
import { usePrdStore } from '../store/prd-store';

export const EditorContent: React.FC = () => {
  const {
    document,
    selectedSectionId,
    selectedRequirementId,
    selectedUserStoryId,
    updateSection,
    updateRequirement,
    updateUserStory,
  } = usePrdStore();

  const editor = useEditor({
    extensions: [
      StarterKit,
      Placeholder.configure({
        placeholder: 'Start writing...',
      }),
      TaskList,
      TaskItem.configure({
        nested: true,
      }),
      Table.configure({
        resizable: true,
      }),
      Link.configure({
        openOnClick: false,
      }),
    ],
    content: '',
    onUpdate: ({ editor }) => {
      const content = editor.getHTML();
      
      if (selectedSectionId) {
        updateSection(selectedSectionId, { content });
      } else if (selectedRequirementId) {
        updateRequirement(selectedRequirementId, { description: content });
      } else if (selectedUserStoryId) {
        // For user stories, we might need a different approach
      }
    },
  });

  // Update editor content when selection changes
  useEffect(() => {
    if (!editor || !document) return;

    let content = '';
    let title = '';

    if (selectedSectionId) {
      const section = document.sections.find(s => s.id === selectedSectionId);
      if (section) {
        content = section.content;
        title = section.title;
      }
    } else if (selectedRequirementId) {
      const requirement = document.requirements.find(r => r.id === selectedRequirementId);
      if (requirement) {
        content = requirement.description;
        title = requirement.title;
      }
    } else if (selectedUserStoryId) {
      const story = document.userStories.find(s => s.id === selectedUserStoryId);
      if (story) {
        content = `
          <h3>${story.title}</h3>
          <p><strong>As a</strong> ${story.asA}</p>
          <p><strong>I want</strong> ${story.iWant}</p>
          <p><strong>So that</strong> ${story.soThat}</p>
          <h4>Acceptance Criteria</h4>
          <ul>
            ${story.acceptanceCriteria.map(c => `<li>${c}</li>`).join('')}
          </ul>
        `;
        title = story.title;
      }
    }

    editor.commands.setContent(content);
  }, [editor, document, selectedSectionId, selectedRequirementId, selectedUserStoryId]);

  if (!document) return null;

  const getSelectedTitle = () => {
    if (selectedSectionId) {
      const section = document.sections.find(s => s.id === selectedSectionId);
      return section?.title || '';
    }
    if (selectedRequirementId) {
      const requirement = document.requirements.find(r => r.id === selectedRequirementId);
      return requirement?.title || '';
    }
    if (selectedUserStoryId) {
      const story = document.userStories.find(s => s.id === selectedUserStoryId);
      return story?.title || '';
    }
    return '';
  };

  return (
    <div className="prd-editor-content h-full flex flex-col bg-white dark:bg-gray-800">
      {/* Selected item header */}
      {(selectedSectionId || selectedRequirementId || selectedUserStoryId) && (
        <div className="border-b px-6 py-4">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
            {getSelectedTitle()}
          </h2>
        </div>
      )}

      {/* Editor */}
      <div className="flex-1 overflow-y-auto">
        {editor ? (
          <div className="prose prose-lg dark:prose-invert max-w-none p-6">
            <TiptapContent editor={editor} />
          </div>
        ) : (
          <div className="p-6 text-center text-gray-500">
            Select a section, requirement, or user story to edit
          </div>
        )}
      </div>

      {/* Editor toolbar */}
      {editor && (
        <div className="border-t px-6 py-2 flex items-center gap-2">
          <button
            onClick={() => editor.chain().focus().toggleBold().run()}
            className={`p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-700 ${
              editor.isActive('bold') ? 'bg-gray-200 dark:bg-gray-700' : ''
            }`}
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 4h8a4 4 0 014 4 4 4 0 01-4 4H6z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 12h9a4 4 0 014 4 4 4 0 01-4 4H6z" />
            </svg>
          </button>
          
          <button
            onClick={() => editor.chain().focus().toggleItalic().run()}
            className={`p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-700 ${
              editor.isActive('italic') ? 'bg-gray-200 dark:bg-gray-700' : ''
            }`}
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 4h4m0 16h-4m4-16l-4 16" />
            </svg>
          </button>
          
          <div className="w-px h-6 bg-gray-300 dark:bg-gray-600 mx-1" />
          
          <button
            onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
            className={`p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-700 ${
              editor.isActive('heading', { level: 2 }) ? 'bg-gray-200 dark:bg-gray-700' : ''
            }`}
          >
            H2
          </button>
          
          <button
            onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
            className={`p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-700 ${
              editor.isActive('heading', { level: 3 }) ? 'bg-gray-200 dark:bg-gray-700' : ''
            }`}
          >
            H3
          </button>
          
          <div className="w-px h-6 bg-gray-300 dark:bg-gray-600 mx-1" />
          
          <button
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            className={`p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-700 ${
              editor.isActive('bulletList') ? 'bg-gray-200 dark:bg-gray-700' : ''
            }`}
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01" />
            </svg>
          </button>
          
          <button
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            className={`p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-700 ${
              editor.isActive('orderedList') ? 'bg-gray-200 dark:bg-gray-700' : ''
            }`}
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 6h13M7 12h13m-13 6h13M4 6h.01M4 12h.01M4 18h.01" />
            </svg>
          </button>
          
          <button
            onClick={() => editor.chain().focus().toggleTaskList().run()}
            className={`p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-700 ${
              editor.isActive('taskList') ? 'bg-gray-200 dark:bg-gray-700' : ''
            }`}
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
            </svg>
          </button>
        </div>
      )}
    </div>
  );
};